import type React from 'react';
import { Outlet } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { AdminSidebar } from './AdminSidebar';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';

/**
 * AdminLayout - Main layout wrapper for admin pages
 *
 * Features:
 * - Sidebar navigation (collapsible on mobile)
 * - Main content area with Outlet
 * - Glassmorphism design aesthetic
 * - Dark mode support
 * - Responsive layout
 *
 * Usage:
 * ```tsx
 * // Used automatically by admin routes via _admin.tsx
 * ```
 */
export const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <div
      data-testid="admin-layout"
      className={cn('min-h-screen', 'bg-background', 'text-foreground')}
    >
      {/* Top header bar - visible on all screen sizes */}
      <header
        className={cn(
          'sticky top-0 z-40',
          'border-b border-border/50',
          'bg-card/80 backdrop-blur-sm',
          'px-4 py-3 md:px-6'
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">Admin Panel</h1>
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <>
                <div className="hidden sm:flex items-center gap-2">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.displayName || 'Admin'}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {user.displayName?.charAt(0).toUpperCase() || 'A'}
                      </span>
                    </div>
                  )}
                  <span className="text-sm font-medium">{user.displayName || user.email}</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  Logout
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content area with sidebar */}
      <div className="flex">
        {/* Sidebar - hidden on mobile, sticky on desktop */}
        <AdminSidebar />

        {/* Main content */}
        <main
          role="main"
          data-testid="admin-main"
          className={cn('flex-1', 'p-4 md:p-6 lg:p-8', 'min-h-[calc(100vh-65px)]')}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};
